/**
 * Dashboard Header
 * Responsive header with hamburger menu for mobile
 */

import { useTranslation, useSelector, useDispatch, useNavigate } from '@ufc/core'
import { useTheme, useSidebar } from '@/providers'
import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LogOut,
  Settings,
  User as UserIcon,
} from '@ufc/uikit'
import {
  ThemeToggle,
  LanguageToggle,
  UserProfile,
  MobileUserAvatar,
  MobileLogo,
  MobileMenuButton,
  TenantSelector,
  LocationSelector,
} from './components'
import { SearchTrigger, GlobalSearchDialog } from '@ufc/domains/global-search'
import { isRtlLanguage } from '@ufc/portal-shared'

// Auth
import { authActions, selectAuthUser } from '@ufc/domains/auth'
import { selectProfile } from '@ufc/domains/portal-init'
import { LOCATION_TYPE_CODES } from '@ufc/domains/locations'

// ============================================
// Helper Functions
// ============================================

function getUserDisplayName(user: { username?: string; email?: string } | null, isArabic: boolean): string {
  if (!user) return isArabic ? 'ضيف' : 'Guest'
  if (user.username) return user.username
  if (user.email) return user.email.split('@')[0]
  return isArabic ? 'مستخدم' : 'User'
}

type NavigateFn = ReturnType<typeof useNavigate>

function goToSettingsTab(navigate: NavigateFn, tab: 'profile' | 'preferences') {
  navigate({ to: '/settings', search: { tab } })
}

// ============================================
// Main Component
// ============================================

export function DashboardHeader() {
  const { t, i18n } = useTranslation()
  const { theme, toggleTheme } = useTheme()
  const { openMobile, isMobile } = useSidebar()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const user = useSelector(selectAuthUser)
  const profile = useSelector(selectProfile)
  const isRTL = isRtlLanguage(i18n.language)

  const handleLogout = () => {
    dispatch(authActions.logout())
  }

  const handleLanguageChange = (language: string) => {
    i18n.changeLanguage(language)
    if (typeof window !== 'undefined') {
      localStorage.setItem('i18nextLng', language)
    }
  }

  const userName = getUserDisplayName(user, isRTL)

  return (
    <header
      className={cn(
        'glass-strong rounded-2xl px-4 md:px-6 flex items-center justify-between gap-4',
        'h-16 md:h-20 mx-4 mt-0 md:mt-2'
      )}
    >
      {/* Left section */}
      <div className="flex items-center gap-3 flex-1">
        {isMobile && <MobileMenuButton onClick={openMobile} />}

        {!isMobile && <SearchTrigger isRTL={isRTL} />}

        {isMobile && <MobileLogo />}
      </div>

      {/* Right section */}
      <div className="flex items-center gap-2 md:gap-3">
        {/* Location Selector — factories only (customer portal); other portals use full list */}
        {!isMobile && !profile?.locationDocumentId && (
          <LocationSelector allowedLocationTypeCodes={[LOCATION_TYPE_CODES.FACTORY_LOCATION]} />
        )}

        {/* Tenant Selector - only visible to admins on desktop */}
        {!isMobile && <TenantSelector />}

        <ThemeToggle
          theme={theme}
          onToggle={toggleTheme}
          isMobile={isMobile}
        />

        <LanguageToggle
          currentLang={i18n.language}
          onChange={handleLanguageChange}
          isMobile={isMobile}
        />

        {/* User Menu with Dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button className="focus:outline-none">
              {!isMobile ? (
                <UserProfile
                  name={userName}
                  role={t('header.customer')}
                  isRTL={isRTL}
                />
              ) : (
                <MobileUserAvatar />
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align={isRTL ? 'start' : 'end'} className="w-56">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{userName}</p>
              {user?.email && (
                <p className="text-xs text-muted-foreground">{user.email}</p>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => goToSettingsTab(navigate, 'profile')}>
              <UserIcon className="w-4 h-4 me-2" />
              {t('header.profile')}
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => goToSettingsTab(navigate, 'preferences')}>
              <Settings className="w-4 h-4 me-2" />
              {t('header.settings')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
              <LogOut className="w-4 h-4 me-2" />
              {t('header.logout')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}

// Re-export types
export type * from './types'
